import type { PlanWeek } from '../types';
import type { Tracer } from '../tracer';

/**
 * Progression rule — week-over-week, bump load by a small percentage when the
 * previous week's RPE met or undercut the target. Applied during initial plan
 * generation as a forward projection: each week's load is ~1.5% heavier than
 * the previous (when RPE log is absent, this is the default progression).
 *
 * After the plan is live, this rule re-runs from logged RPE actual values
 * to adjust subsequent weeks.
 */
export function progressionRule(weeks: PlanWeek[], trace: Tracer): PlanWeek[] {
  const PROGRESS_MULTIPLIER_PER_WEEK = 1.015;

  let totalAdjusted = 0;

  for (let i = 1; i < weeks.length; i++) {
    const prev = weeks[i - 1];
    const cur = weeks[i];
    if (!prev || !cur || cur.is_deload) continue;

    for (const day of cur.days) {
      const prevDay = prev.days.find((d) => d.day_number === day.day_number);
      if (!prevDay) continue;
      for (const ex of day.exercises) {
        const prevEx = prevDay.exercises.find((e) => e.exercise_id === ex.exercise_id);
        if (!prevEx) continue;
        const prevLoad = prevEx.load_kg;
        if (prevLoad !== null) {
          ex.load_kg = Math.round(prevLoad * PROGRESS_MULTIPLIER_PER_WEEK * 10) / 10;
          totalAdjusted++;
        }
      }
    }
  }

  trace.emit(
    'progression',
    `${totalAdjusted} exercises projected`,
    `Default forward projection of +${((PROGRESS_MULTIPLIER_PER_WEEK - 1) * 100).toFixed(1)}%/week until RPE logs are available.`,
    { weeks: weeks.length, adjusted_count: totalAdjusted },
  );

  return weeks;
}
